import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Alert,
  IconButton,
  Card,
  CardContent,
  Divider,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import RefreshIcon from "@mui/icons-material/Refresh";
import LaunchIcon from "@mui/icons-material/Launch";
import StorageIcon from "@mui/icons-material/Storage";
import DnsIcon from "@mui/icons-material/Dns";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";
import DashboardLayout from "../../layouts/DashboardLayout";
import MDBox from "../../components/MDBox";
import MDButton from "../../components/MDButton";

import { usersAPI } from "../../services/api";

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    username: "",
    full_name: "",
    email: "",
    password: "",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.listUsers();
      setUsers(response.data);
    } catch (err) {
      console.error("Fehler beim Abrufen der Benutzer:", err);
      setErrorMsg(
        "Fehler beim Abrufen der Benutzer. Bitte versuchen Sie es erneut.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    setEditUser(user);
    setFormData(
      user
        ? {
            username: user.username || "",
            full_name: user.full_name || "",
            email: user.email || "",
            password: "",
          }
        : {
            username: "",
            full_name: "",
            email: "",
            password: "",
          },
    );
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditUser(null);
    setFormData({
      username: "",
      full_name: "",
      email: "",
      password: "",
    });
    setErrorMsg("");
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSaveUser = async () => {
    setErrorMsg("");
    if (!formData.username || !formData.full_name || !formData.email) {
      setErrorMsg("Bitte füllen Sie alle erforderlichen Felder aus.");
      return;
    }
    if (!validateEmail(formData.email)) {
      setErrorMsg("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }
    if (!editUser && !formData.password) {
      setErrorMsg("Passwort ist für neue Benutzer erforderlich.");
      return;
    }

    try {
      if (editUser) {
        // Update user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send empty password
        }
        await usersAPI.updateUser(editUser.id, updateData);
        setSuccessMsg("Benutzer erfolgreich aktualisiert.");
      } else {
        // Add new user
        await usersAPI.createUser(formData);
        setSuccessMsg("Neuen Benutzer erfolgreich hinzugefügt.");
      }
      handleCloseDialog();
      fetchUsers(); // Refresh the list
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      console.error("Fehler beim Speichern des Benutzers:", err);
      setErrorMsg(
        "Fehler beim Speichern des Benutzers. Bitte versuchen Sie es erneut.",
      );
    }
  };

  const handleDeleteUser = async (userId) => {
    if (
      !window.confirm(
        "Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?",
      )
    ) {
      return;
    }
    try {
      await usersAPI.deleteUser(userId);
      setSuccessMsg("Benutzer erfolgreich gelöscht.");
      fetchUsers(); // Refresh the list
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      console.error("Fehler beim Löschen des Benutzers:", err);
      setErrorMsg(
        "Fehler beim Löschen des Benutzers. Bitte versuchen Sie es erneut.",
      );
    }
  };

  const adminTools = [
    {
      name: "Portainer",
      url: import.meta.env.VITE_PORTAINER_URL,
      icon: <DnsIcon fontSize="large" />,
      description: "Container Management",
    },
    {
      name: "Nginx Proxy Manager",
      url: import.meta.env.VITE_NGINX_URL,
      icon: <SecurityIcon fontSize="large" />,
      description: "Reverse Proxy & SSL",
    },
    {
      name: "PgAdmin",
      url: import.meta.env.VITE_PGADMIN_URL,
      icon: <StorageIcon fontSize="large" />,
      description: "Database Administration",
    },
    {
      name: "Grafana",
      url: import.meta.env.VITE_GRAFANA_URL,
      icon: <AssessmentIcon fontSize="large" />,
      description: "Metrics Dashboard",
    },
    {
      name: "Prometheus",
      url: import.meta.env.VITE_PROMETHEUS_URL,
      icon: <SpeedIcon fontSize="large" />,
      description: "Time Series Database",
    },
    {
      name: "Uptime Kuma",
      url: import.meta.env.VITE_UPTIME_KUMA_URL,
      icon: <MonitorHeartIcon fontSize="large" />,
      description: "Uptime Monitoring",
    },
  ];

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <MDBox mb={6}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Interne Administrationstools
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4}>
            Direkter Zugriff auf Infrastruktur-, Datenbank- und Überwachungs-
            dashboards.
          </Typography>

          <Grid container spacing={3}>
            {adminTools.map((tool) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} key={tool.name}>
                <Card
                  elevation={3}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      p: 3,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "info.main",
                        width: 56,
                        height: 56,
                        mb: 2,
                        boxShadow: 3,
                      }}
                    >
                      {tool.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {tool.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3, px: 2 }}
                    >
                      {tool.description}
                    </Typography>
                    <Box mt="auto" width="100%">
                      <Button
                        variant="outlined"
                        color="info"
                        href={tool.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<LaunchIcon />}
                        fullWidth
                      >
                        Dashboard öffnen
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </MDBox>

        <Divider sx={{ my: 4 }} />

        <MDBox mb={4}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Benutzerverwaltung
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Benutzerkonten verwalten, neue hinzufügen, bestehende bearbeiten
            oder entfernen.
          </Typography>
        </MDBox>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMsg}
          </Alert>
        )}
        {successMsg && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMsg}
          </Alert>
        )}

        <Box display="flex" justifyContent="flex-end" mb={4} gap={2}>
          <MDButton
            variant="outlined"
            color="info"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            size="large"
            disabled={loading}
          >
            Aktualisieren
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="large"
          >
            Neuen Benutzer hinzufügen
          </MDButton>
        </Box>

        <Card elevation={4}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                <PeopleIcon />
              </Avatar>
              <Typography variant="h5" fontWeight="bold">
                Alle Benutzer
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <TableContainer component={Paper} elevation={2}>
              <Table sx={{ minWidth: 650 }} aria-label="users table">
                <TableHead sx={{ bgcolor: "grey.100" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Benutzername
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Vollständiger Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      E-Mail
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "bold", fontSize: "1rem" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Benutzer werden geladen...
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow
                        key={user.id}
                        sx={{ "&:hover": { bgcolor: "grey.50" } }}
                      >
                        <TableCell sx={{ fontSize: "0.95rem" }}>
                          {user.username}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.95rem" }}>
                          {user.full_name}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.95rem" }}>
                          {user.email}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={() => handleOpenDialog(user)}
                            color="info"
                            size="large"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteUser(user.id)}
                            color="error"
                            size="large"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* User Dialog */}
        <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            {editUser ? "Benutzer bearbeiten" : "Neuen Benutzer erstellen"}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Benutzername"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                sx={{ mb: 3 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                label="Vollständiger Name"
                value={formData.full_name}
                onChange={(e) =>
                  setFormData({ ...formData, full_name: e.target.value })
                }
                sx={{ mb: 3 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                sx={{ mb: 3 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                label={
                  editUser
                    ? "Neues Passwort (leer lassen, um das aktuelle beizubehalten)"
                    : "Passwort"
                }
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                sx={{ mb: 2 }}
                variant="outlined"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseDialog} size="large">
              Abbrechen
            </Button>
            <MDButton
              onClick={handleSaveUser}
              variant="contained"
              color="info"
              size="large"
              disabled={
                loading ||
                formData.username === "" ||
                formData.full_name === "" ||
                formData.email === "" ||
                (!editUser && formData.password === "")
              }
            >
              {editUser ? "update" : "create"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
};

export default AdminPage;
